import {
  HeadContent,
  Outlet,
  createRootRouteWithContext,
} from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import TanstackQueryLayout from '../integrations/tanstack-query/layout'
import type { QueryClient } from '@tanstack/react-query'
import type { User } from 'firebase/auth'
import type { PermissionExpression } from '@/data/entities'
import { Toaster } from '@/components/ui/sonner'
import RootErrorComponent from '@/components/RootErrorComponent'
import { useIsMobile } from '@/hooks/use-mobile'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useDevModeStore } from '@/stores/useDevModeStore'

interface MyRouterContext {
  queryClient: QueryClient
  authState: User | null
  TENANT_ID: string | null
  isOrgAdmin: boolean
  isOrgEditor: boolean
  isSysAdmin: boolean
  hasAnyEditorAccess: boolean
  permissionExpression: PermissionExpression | null
  availableTenants: TenantInfo[]
}

// Type definition for tenant information
type TenantInfo = {
  id: string
  name: string
  description: string
}

function Root() {
  const isMobile = useIsMobile()
  const { devMode } = useDevModeStore()
  return (
    <>
      <HeadContent />
      <TooltipProvider>
        <Outlet />
      </TooltipProvider>
      <Toaster
        position={isMobile ? 'top-center' : 'top-right'}
        toastOptions={{
          unstyled: true,
          classNames: {
            toast: 'w-96 flex gap-2 rounded-lg border border-border p-4',
            icon: 'translate-y-0.5',
            title: 'font-medium text-sm',
            description: 'text-sm',
            success: '!bg-success-light text-success border-success',
            error: '!bg-destructive-light text-destructive border-destructive',
            default: 'bg-white',
            actionButton:
              'ml-auto shrink-0 rounded-md bg-white px-3 py-1.5 text-xs font-medium text-foreground border border-border hover:bg-gray-50 cursor-pointer',
          },
        }}
      />
      {devMode && !isMobile && (
        <>
          <TanStackRouterDevtools />
          <TanstackQueryLayout />
        </>
      )}
    </>
  )
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: Root,
  head: () => ({
    title: 'LineWise',
    meta: [
      {
        title: 'LineWise',
      },
      {
        name: 'description',
        content: 'LineWise Web App',
      },
    ],
  }),
  errorComponent: ({ error }) => <RootErrorComponent error={error} />,
})
